import { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useSavingGoalsStore } from '@flows.cash/core';
import { Transaction, Goal } from '@flowscash/types';
import { formatCurrency } from '@/utils/formatters';
import { PiggyBank, Check } from 'lucide-react';
import { toast } from 'sonner';

interface AssignToGoalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction;
}

export default function AssignToGoalDialog({ isOpen, onClose, transaction }: AssignToGoalDialogProps) {
  const { goals, assignTransactionToGoal, isLoading } = useSavingGoalsStore();
  const [assigningGoalId, setAssigningGoalId] = useState<string | null>(null);
  
  const handleAssign = async (goalId: string) => {
    try {
      setAssigningGoalId(goalId);
      await assignTransactionToGoal({ goalId, transactionId: transaction.id });
      toast.success('Transaction assigned to goal successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to assign transaction to goal');
      console.error(error);
    } finally {
      setAssigningGoalId(null);
    }
  };

  // Filter out completed goals
  const availableGoals = goals.filter(goal => !goal.is_completed);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-6 bg-white dark:bg-[#182438] rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-[#1C1C1C] dark:text-[#E8E8E8]">
            Assign to Saving Goal
          </DialogTitle>
          <DialogDescription className="text-[#2F2F2F] dark:text-[#A3A3A3]">
            Choose a saving goal to assign this transaction to
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          {availableGoals.length > 0 ? (
            availableGoals.map((goal) => (
              <Button
                key={goal.id}
                variant="outline"
                className="w-full justify-between p-4 h-auto"
                disabled={isLoading || assigningGoalId === goal.id}
                onClick={() => handleAssign(goal.id)}
              >
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F3F4F6] dark:bg-[#333840]">
                    {goal.icon ? (
                      <span>{goal.icon}</span>
                    ) : (
                      <PiggyBank className="w-4 h-4 text-[#2F2F2F] dark:text-[#A3A3A3]" />
                    )}
                  </span>
                  <div className="text-left">
                    <div className="font-medium text-[#1C1C1C] dark:text-[#E8E8E8]">{goal.name}</div>
                    <div className="text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                      {formatCurrency(goal.current_amount, goal.currency)} of {formatCurrency(goal.target_amount, goal.currency)}
                    </div>
                  </div>
                </div>
                
                {assigningGoalId === goal.id && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#2F2F2F] dark:border-[#A3A3A3]" />
                )}
              </Button>
            ))
          ) : (
            <div className="text-center py-8 text-[#2F2F2F] dark:text-[#A3A3A3]">
              <PiggyBank className="w-12 h-12 mx-auto mb-3 text-[#2F2F2F] dark:text-[#A3A3A3] opacity-50" />
              <p className="font-medium text-[#1C1C1C] dark:text-[#E8E8E8]">No active goals found</p>
              <p className="text-sm mt-1">Create a saving goal first to assign transactions</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 